import { AfterViewInit, Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { SohoButtonComponent } from 'ids-enterprise-ng';
import {
  LMCompactFormEvent,
  LMFieldBaseComponent,
  LMFieldValueChangedEvent,
  LMSubFormEvent
} from '@field/lm-field-base.component';
import { LMModel } from '@form/model/lm-model';
import { LMFieldServiceAdapter } from '@field/lm-field-service-adapter';
import { AppConstants, LMButtonType } from '@utils/app.constants';
import { LMModelChangedEvent } from '@form/model';

export class LMButtonEvent implements LMFieldEvent {
  layoutObject: LMButtonLayout;
  event: Event;
  eventType: LMFieldEventType;

  constructor(event: Event, layoutObject: LMButtonLayout) {
    this.eventType = 'ButtonEvent';
    this.event = event;
    this.layoutObject = layoutObject;
  }
}

enum LMButtonSubTypeDisplayEnum {
  ICON_BUTTON = 0,
  TEXT_BUTTON,
  TEXT_MAIL_BUTTON,
  ICON_MAIL_BUTTON,
  TEXT_LINK,
  MAIL_LINK
}

@Component({
  selector:    'lm-button',
  templateUrl: './lm-button.component.html',
  styleUrls:   [ './lm-button.component.scss' ]
})
export class LMButtonComponent extends LMFieldBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class.field') get isField() {
    return true;
  };
  @HostBinding('class.lm-single-column-child') isChildOfSingleColumn = false;
  @HostBinding('class.lm-icon-button') isIconButton = false;

  @HostBinding('class.lm-button-right') get isAlignRight() {
    return this.buttonAlign === 'right';
  }
  @HostBinding('class.lm-button-center') get isAlignCenter() {
    return this.buttonAlign === 'center';
  }
  @HostBinding('class.lm-button-left') get isAlignLeft() {
    return this.buttonAlign === 'left';
  }

  @HostBinding('attr.data-automation-id') get dataFieldAutomationId() {
    return `lm-${this.fieldAutomationId}-button`;
  }

  @Input() layoutObject: LMButtonLayout;
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;

  @Output() fieldEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();

  public displayAsButton = true;
  public displayAsLink = false;
  public mailToHttpUrl = false;
  public text: string;
  public buttonIcon: string = null;
  public buttonType: string = null;
  public iconHref: string;
  public iconUrl: string;
  public validSohoIcon: boolean;
  public valid = false;
  public isDataViewFieldButton = false;
  public dataViewFieldButtonHasData = false;

  public displayType: LMButtonSubTypeDisplayEnum;
  public layoutDisplayType;

  private buttonAlign: HorizontalAlignment = null;

  constructor(protected lmFieldServiceAdapter: LMFieldServiceAdapter) {
    super(lmFieldServiceAdapter);
    this.layoutDisplayType = LMButtonSubTypeDisplayEnum;
  }

  // events
  ngOnInit() {
    super.ngOnInit();
    this.initialize(this.layoutObject, this.specFields, this.model);

    if (this.lmColumnLayoutInfo && this.lmColumnLayoutInfo.nbrColumns === 1) {
      this.isChildOfSingleColumn = true;
    }

    if (this.layoutObject.hasOwnProperty('buttonType')) {
      this.buttonType = this.layoutObject.buttonType;

    } else if (this.layoutObject.iconUrl && !this.layoutObject.title) {
      this.buttonType = SohoButtonComponent.ICON;

    } else {
      this.buttonType = this.layoutObject.isPrimary ? SohoButtonComponent.PRIMARY : SohoButtonComponent.SECONDARY;
    }

    if (this.field && this.field.fieldStyle && this.field.fieldStyle.align) {
      this.buttonAlign = this.field.fieldStyle.align;
    }

    this.buttonIcon = this.layoutObject.icon;

    if (this.buttonIcon === 'internalCorrespondenceIconBlack') {
      this.buttonIcon = 'mail';
    }

    this.iconHref = '';

    if (this.layoutObject.httpUrl && this.layoutObject.httpUrl.resourceValue.indexOf('mailto:') === 0) {
      this.mailToHttpUrl = true;
    }

    if (this.layoutObject.iconUrl) {
      this.iconUrl = 'url(' + this.layoutObject.iconUrl + ')';
    }

    if (this.layoutObject.icon) {
      this.validSohoIcon = this.lmFieldServiceAdapter.lmThemeService.isValidSohoIcon(this.layoutObject.icon);
    }

    if (this.layoutObject.displayAsLink) {
      this.displayAsLink = true;
      this.displayAsButton = false;
    }

    if (this.displayAsButton && !this.mailToHttpUrl) {
      if (this.iconUrl) {
        this.displayType = this.layoutDisplayType.ICON_BUTTON;
        this.isIconButton = !this.layoutObject.hasOwnProperty('title');
      } else {
        this.displayType = this.layoutDisplayType.TEXT_BUTTON;

      }
    } else if (this.displayAsButton && this.mailToHttpUrl) {
      if (this.iconUrl) {
        this.displayType = this.layoutDisplayType.ICON_MAIL_BUTTON;
        this.isIconButton = !this.layoutObject.hasOwnProperty('title');
      } else {
        this.displayType = this.layoutDisplayType.TEXT_MAIL_BUTTON;

      }
    } else if (this.displayAsLink && !this.mailToHttpUrl) {
      this.displayType = this.layoutDisplayType.TEXT_LINK;

    } else if (this.displayAsLink && this.mailToHttpUrl) {
      this.displayType = this.layoutDisplayType.MAIL_LINK;
    }

    this.valid = this.checkValidity();

    this.setDataViewFieldButtonState();

    this.subscriptionArray.push(this.model.modelChanged
      .subscribe((modelChangedEvent: LMModelChangedEvent) => {
        // triggerValue changes to indicate the formatMessageRule pipe to fire
        // triggerValue is set in lm-field-base.component; do not set it here

        this.valid = this.checkValidity();

        this.setDataViewFieldButtonState();
      })
    );
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  // duplicate code in lm-hyperlink
  // FYI.  Rich client does NOT check validity of a navigation button on a form
  checkValidity() {
    let valid = true;

    // if (this.field.type === AppConstants.BUTTON_TYPE_NAVIGATION) {
    //   // TODO: LMCLIENT-21757, LMCLIENT-23380, LMCLIENT-25651; temp work around until LMRK-83694 is completed
    //   if (this.lmFieldServiceAdapter.lmFormService.isStepProcessForm(this.model.formSpec)) {
    //     return true;
    //   }
    //
    //   valid = !this.lmFieldServiceAdapter.lmConditionUtilService.isNavigationInvalid(this.layoutObject, this.model);
    // }

    if (this.layoutObject.validity) {
      valid = this.lmFieldServiceAdapter.lmConditionUtilService.isValid(this.layoutObject.validity as LMCondition[], this.model);
    }

    if (this.layoutObject.isStandardAction) {
      if (this.layoutObject.standardActionName !== AppConstants.STANDARD_SAVE) {
        // only save supported, disable all other standard actions
        valid = false;
      }
    }

    return valid;
  }

  setDataViewFieldButtonState() {
    if (this.layoutObject.hasOwnProperty('dataViewFieldButton')) {
      this.isDataViewFieldButton = true;
      this.dataViewFieldButtonHasData = (!!this.modelField.value && this.modelField.value.trim() !== '');
    }
  }

  mailTo(event: Event) {
    const messageRule = this.layoutObject.httpUrl;
    const email = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(messageRule, this.model, this.specFields);
    window.location.href = email;
  }

  onClickEvent(event: Event) {
    if (this.mailToHttpUrl) {
      this.mailTo(event);

      // TODO: need to evaluate this again; re: LMCLIENT-26683:
    } else if (this.compactFormDialogSpec) {
      this.fieldEvent.emit(new LMCompactFormEvent(event, this.layoutObject.fieldId, this.compactFormDialogSpec.label,
        this.compactFormDialogSpec.layout, this.layoutObject.moreSpecFields));

    } else if (this.layoutObject.type === LMButtonType.SHOWEDITOR && this.modelField.isCompoundField) {
      const title = this.specFields[ this.layoutObject.fieldId ].label;
      const layout: LMFormLayout = { sections: this.layoutObject.layout as Array<LMFormLayoutSection> };

      this.fieldEvent.emit(new LMCompactFormEvent(event, this.field.fieldId, title, layout, this.layoutObject.moreSpecFields));

    } else if (this.isSubForm) {
      this.fieldEvent.emit(new LMSubFormEvent(event, this.layoutObject.fieldId, this.field.buttonSubForm.title,
        this.field.buttonSubForm));
    } else if (this.layoutObject.fireInitialValue) {
      this.fireInitialValueRule();
    } else {
      this.fieldEvent.emit(new LMButtonEvent(event, this.layoutObject));
    }
  }

  fireInitialValueRule() {
    if (this.layoutObject.fireInitialValue) {
      this.fireWhenValueChangedEvent(this.bindId);
    }
  }

  private fireWhenValueChangedEvent(bindId: string) {
    this.fieldEvent.emit(
      new LMFieldValueChangedEvent(
        new CustomEvent('ValueChanged'), 'FireInitialValueRuleEvent', bindId));
  }
}
